import React from 'react';
import _, { get, noop } from 'lodash';
import fp from 'lodash/fp';
import type { GridCellProps, Size } from 'react-virtualized';
import { Grid } from 'react-virtualized/dist/es/Grid';
import { AutoSizer } from 'react-virtualized/dist/es/AutoSizer';
import { classes } from 'typestyle';

import { Overlay } from 'src/common-ui';

import Renderer from 'src/utils/Domain/Renderer';
import Subheader from 'src/components/Subheader/Subheader.container';
import { ContainerPayload } from 'src/components/RightContainer/RightContainer.slice';
import { SubheaderSlice, SubheaderViewDefns } from 'src/components/Subheader/Subheader.slice';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { formatSummaries } from 'src/utils/Pivot/RollUp';
import { GetSummaryOutput } from 'src/pages/Hindsighting/StyleColorReview/CollectionView/CollectionView.selectors';

import { CardViewGroup, CardViewItem } from './UIData.types';
import { PrintProps } from '../higherOrder/Print/Print';
import { StandardCard, StandardCardProps } from './StandardCard/StandardCard';
import { styles } from './StandardCardView.styles';
import { StyleDetailsPopoverProps } from '../AssortmentStyleDetailsPopover/AssortmentStyleDetailsPopover';
import { isNil } from 'lodash';
import { HINDSIGHTING, ASSORTMENT_BUILD } from 'src/utils/Domain/Constants';
import { ViewConfiguratorModalProps } from '../ViewConfiguratorModal/ViewConfiguratorModal';
import { FabHandlerInvocable, FabProps } from 'src/components/higherOrder/withFab';

import { BackgroundDataLoadingProps } from '../BackgroundDataLoading/BackgroundDataLoading';
import { IS_HINDSIGHTING } from 'src/utils/Domain/ConstantsFunctions';
import { NO_GROUPS_TYPE } from './CardViewDataProcessor';
import { RenderedGridCard } from 'src/common-ui/components/CardsGrid/CardsGrid';
import { AdornmentType } from 'configuration-schemas/viewdefns/literals';
import { getSectionContext } from 'src/utils/Domain/Perspective';
import { ColumnGroupedViewOwnProps } from 'configuration-schemas/ownProps';

interface Props
  extends ColumnGroupedViewOwnProps,
    StateProps,
    DispatchProps,
    PrintProps,
    Omit<StyleDetailsPopoverProps, 'onItemClicked'>,
    FabHandlerInvocable {}

type SubheaderPicks = 'groupBy' | 'sortBy';

export interface StateProps extends Pick<SubheaderSlice, SubheaderPicks>, BackgroundDataLoadingProps {
  hideTitle?: boolean;
  groupedStyles: CardViewGroup[];
  config: TenantConfigViewData;
  unmodifiedViewDefn: TenantConfigViewData;
  subheaderViewDefns: SubheaderViewDefns;
  showCountLimit?: boolean;
  loaded: boolean;
  fabType?: string;
  summary: GetSummaryOutput[];
  subheaderCustomEl?: JSX.Element | undefined;
  currentTab?: string;
  isFabDisabled: boolean;
  downloadLink?: string;
  adornments: AdornmentType[];
  fab: FabProps;
}

export interface DispatchProps {
  onShowView(): void;
  onItemClicked(item: ContainerPayload, eventTarget?: HTMLElement): void;
  showStylePane(item: ContainerPayload): void;
  onConfigUpdate(config: TenantConfigViewData): void;
}

class ColumnGroupedView extends React.Component<Props> {
  state: {
    groupScroll: number[];
    groupCells: JSX.Element | undefined;
  };
  inCleanup!: boolean;

  constructor(props: Props) {
    super(props);
    this.state = {
      groupScroll: [],
      groupCells: undefined,
    };
    this.renderCards = this.renderCards.bind(this);
    this.render = this.render.bind(this);
    this.verticalGroupRender = this.verticalGroupRender.bind(this);
  }

  componentDidMount() {
    this.props.onShowView();
  }

  onFabClick = () => {
    // currently no views with this component require cart/worklist handling
    noop();
  };

  static buildGroupheaders = (groups: CardViewGroup[], summary: GetSummaryOutput[]) => {
    const widthValue = `${100 / groups.length}%`;

    return groups.map((group, i) => (
      <hgroup key={i} style={{ width: widthValue }} className={classes(styles.groupHeader, styles.colGroupHeader)}>
        <h1>{group.header}</h1>
        <h2>
          {fp
            .map((calc) => {
              const lol = summary.find((item) => item.label === calc.title);
              let percentageText = '';
              if (calc.includePercentage && lol) {
                percentageText = ' (' + Renderer.percent(calc.raw / lol.raw, '0.00') + ')';
              }
              return calc.title + ': ' + calc.value + percentageText;
            }, group.groupCalcs)
            .join(' | ') || '\u00A0'}
        </h2>
      </hgroup>
    ));
  };

  static getRowList = (numCards: number, groups: CardViewGroup[]) => {
    const rowCount = Math.max(...groups.map((group) => Math.ceil(group.items.length / numCards)));
    return fp.range(0, rowCount).map((ind): CardViewItem[] => {
      return fp.flatten(
        groups.map((group) => {
          return fp.range(0, numCards).map((i) => fp.get(numCards * ind + i, group.items));
        })
      );
    });
  };

  verticalGroupRender({
    groups,
    cardWidth,
    cardHeight,
  }: {
    groups: CardViewGroup[];
    cardWidth: number;
    cardHeight: number;
  }) {
    const SCROLLBARWIDTH = 21;
    const minimumWidth = groups.length * cardWidth + SCROLLBARWIDTH;
    const { summary } = this.props;
    const numCardsPerColRow = (totalWidth: number) => Math.floor(totalWidth / groups.length / cardWidth);

    const cellRenderer = (
      rowList: CardViewItem[][],
      numCardsPerCol: number,
      { columnIndex, rowIndex, key, style, isScrolling, isVisible }: GridCellProps
    ) => {
      const itemFound = !isNil(rowList[rowIndex]) && !isNil(rowList[rowIndex][columnIndex]);
      // Potentially renders an intentional blank card if no item data is found at the current row/column position
      const item = itemFound ? rowList[rowIndex][columnIndex] : ({ isBlankCard: true } as CardViewItem);
      const className =
        (columnIndex + 1) % numCardsPerCol === 0 && columnIndex !== numCardsPerCol * groups.length - 1
          ? classes(styles.colCard, styles.colRightCard)
          : styles.colCard;

      return (
        <RenderedGridCard
          key={key}
          style={style}
          classNameOverride={className}
          isScrolling={isScrolling}
          isVisible={isVisible}
          cardType={'StandardCard'}
          cardProps={this.generateCardProps(item)}
        />
      );
    };

    const autoSizerContent = (size: Size) => {
      const { width, height } = size;
      if (width === 0 || height === 0) {
        return <div>Something went wrong</div>;
      }
      const cardsPerColRow = numCardsPerColRow(width);
      const cellRendererWithRows = fp.curry(cellRenderer)(
        ColumnGroupedView.getRowList(cardsPerColRow, groups),
        cardsPerColRow
      );
      const columnCount = groups.length * cardsPerColRow;
      const rowCount = Math.max(...groups.map((group) => Math.ceil(group.items.length / cardsPerColRow)));
      const TOTAL_GRID_WIIDTH = cardWidth * columnCount;
      const GRID_AND_SCROLLBAR_WIDTH = TOTAL_GRID_WIIDTH + 8;
      const GRID_AND_SCROLLBAR_HEIGHT = height - 75;
      return (
        <React.Fragment>
          <article
            className={styles.columnGrouping}
            style={{
              width: TOTAL_GRID_WIIDTH,
              marginLeft: (width - GRID_AND_SCROLLBAR_WIDTH) / 2,
            }}
          >
            <header className={styles.groupsHeader}>{ColumnGroupedView.buildGroupheaders(groups, summary)}</header>
            <Grid
              cellRenderer={(cellRendererArgs: GridCellProps) => cellRendererWithRows(cellRendererArgs)}
              columnCount={columnCount}
              columnWidth={cardWidth}
              height={GRID_AND_SCROLLBAR_HEIGHT}
              rowCount={rowCount}
              rowHeight={cardHeight + 15}
              width={GRID_AND_SCROLLBAR_WIDTH}
              overscanRowCount={3}
              autoContainerWidth={true}
            />
          </article>
        </React.Fragment>
      );
    };

    return (
      <article
        className={styles.colGroupCont}
        style={{
          // Minimum width ensures we can always fit at least *one* of each column on the screen.
          // The +1 is arbitrary, there needs to be at least *some* amount wider than the contents
          // of the autosizer to prevent a second overflow attempt.
          minWidth: minimumWidth + 1,
        }}
      >
        {/*
                TODO: Width is needed so that the groupHeaders
                do not disappear on especific rezise.
              */}
        <AutoSizer style={{ width: 1 }}>{autoSizerContent}</AutoSizer>
      </article>
    );
  }

  // FIXME: cut down on code duplication/consolidate renderCard and generateCardProps as needed
  renderCard = (styleItem: CardViewItem) => {
    const { showStylePane, keys, adornments } = this.props;
    const { idProp, descProp = '' } = keys;
    const name = descProp === 'description' ? styleItem.name : styleItem.styleName;
    const currentContext = getSectionContext();

    const stylePaneKey =
      styleItem.key === 'LY' || styleItem.key === 'LAST YEAR' || IS_HINDSIGHTING(currentContext)
        ? HINDSIGHTING
        : ASSORTMENT_BUILD;

    return (
      <StandardCard
        key={styleItem[idProp]}
        id={styleItem[idProp]}
        name={name}
        styleName={styleItem.styleName}
        styleId={styleItem.styleId}
        stars={styleItem.stars}
        value={styleItem.value}
        valueRenderer={styleItem.valueRenderer}
        description={styleItem[descProp]}
        styleDescription={styleItem.description}
        imgSrc={styleItem.imgSrc}
        columns={styleItem.columns}
        onMoreInfoClick={showStylePane}
        stylePaneKey={stylePaneKey}
        onItemClick={this.props.onItemClicked}
        departmentId={styleItem.departmentId}
        swatchIds={styleItem.swatchIds}
        adornments={adornments}
      />
    );
  };

  generateCardProps = (styleItem: CardViewItem): StandardCardProps => {
    const { showStylePane, onItemClicked, keys, adornments, allowWorklistFunctionality } = this.props;
    const { idProp, descProp = '' } = keys;
    const name = descProp === 'description' ? styleItem.name : styleItem.styleName;
    const currentContext = getSectionContext();

    const stylePaneKey =
      styleItem.key === 'LY' || styleItem.key === 'LAST YEAR' || IS_HINDSIGHTING(currentContext)
        ? HINDSIGHTING
        : ASSORTMENT_BUILD;

    const cardProps: StandardCardProps = {
      ...styleItem,
      id: styleItem[idProp],
      name,
      description: styleItem[descProp],
      styleDescription: styleItem.description,
      imgSrc: styleItem.imgSrc,
      onMoreInfoClick: showStylePane,
      stylePaneKey: stylePaneKey,
      onItemClick: onItemClicked,
      adornments,
      allowWorklistFunctionality,
    };

    return cardProps;
  };

  renderCards(items: CardViewItem[]) {
    const cards = items.map((item) => {
      const cardProps = this.generateCardProps(item);
      return <StandardCard key={item[this.props.keys.idProp]} {...cardProps} />;
    });
    return cards;
  }

  render() {
    const {
      title,
      hideTitle,
      loaded,
      groupedStyles,
      config,
      subheaderViewDefns,
      summary,
      showCountLimit,
      isPrintMode = false,
      subheaderCustomEl,
      unmodifiedViewDefn,
    } = this.props;

    // Determine if any group has at least one item
    const hasData = groupedStyles.some((group) => group.items && group.items.length > 0);

    const renderAsGroup =
      groupedStyles.length > 1 || (groupedStyles.length > 0 && groupedStyles[0].header !== NO_GROUPS_TYPE);
    const visibleViewItems = get(config, 'view', []).filter((x) => x.visible !== false);
    const width = StandardCard.calcCardWidth(visibleViewItems.length);
    let sizing;
    if (renderAsGroup) {
      if (!isPrintMode) {
        sizing = this.verticalGroupRender({
          groups: groupedStyles,
          cardWidth: width,
          cardHeight: StandardCard.calcCardHeight(),
        });
      } else {
        // columnGroupedView print mode
        if (!config || !config.view) {
          sizing = <div />; // bail out
        } else {
          const groups = groupedStyles;

          // card stuff
          const pageWidth = this.props.printWidth ? parseFloat(this.props.printWidth) * 120 : 0;

          const colCount = groupedStyles.length;
          const cardsPerRow = Math.floor(pageWidth / width / colCount) || 1;

          const horizontalBorderSpace = colCount * 2;
          const columnGroupedWidth = width * (colCount * cardsPerRow) + horizontalBorderSpace;
          const cardHeight = StandardCard.calcCardHeight();

          const cellRenderer = (card: CardViewItem) => {
            const cardStyle = {
              display: 'inline-block',
              verticalAlign: 'top',
              height: cardHeight,
            };

            return (
              <div style={cardStyle} key={_.uniqueId()} className={styles.colCard}>
                {!_.isUndefined(card) ? this.renderCard(card) : <div />}
              </div>
            );
          };

          const cardGroupsContainer = () => (
            <div className={styles.cardGroupsContainer} data-qa="ColumnGroupedViewGroupedGrid">
              {groups.map((group, idx, array) => (
                /* eslint-disable-next-line */
                <div className={`group-container ${idx !== array.length - 1 ? 'border-right' : ''}`}>
                  {group.items.map((card) => cellRenderer(card))}
                </div>
              ))}
            </div>
          );

          sizing = (
            <article className={styles.columnGrouping} style={{ width: columnGroupedWidth, margin: 'auto' }}>
              <header className={styles.groupsHeader}>{ColumnGroupedView.buildGroupheaders(groups, summary)}</header>
              {cardGroupsContainer()}
            </article>
          );
        }
      }
    }

    const viewConfigurator: ViewConfiguratorModalProps = {
      viewConfig: config as TenantConfigViewData,
      unmodifiedViewDefn,
      updateConfig: this.props.onConfigUpdate,
    };

    return (
      <div data-qa-component="ColumnGroupedView" className={isPrintMode ? styles.inPrintMode : styles.mainContainer}>
        <Subheader
          title={title}
          hideTitle={hideTitle}
          groupByDefn={subheaderViewDefns.groupBy}
          sortByDefn={subheaderViewDefns.sortBy}
          countLimitDefn={subheaderViewDefns.countLimit}
          pareDownDefn={subheaderViewDefns.pareDown}
          showFlowStatus={true}
          showCountLimit={showCountLimit}
          summary={formatSummaries(summary)}
          showSearch={true}
          customEl={isNil(subheaderCustomEl) ? undefined : subheaderCustomEl}
          viewConfigurator={viewConfigurator}
          downloadLink={this.props.downloadLink}
          viewDataState={this.props.viewDataState}
        />
        {!loaded && <Overlay type="loading" visible />}

        {loaded &&
          (hasData ? (
            sizing
          ) : (
            // No data found - added for filter tests
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>No Data</div>
          ))}
      </div>
    );
  }
}

export default ColumnGroupedView;
